// The status vocabulary, one source of truth (UI philosophy "Action and status grammar"; Status Labels v05).
// Every state dimension keeps its own map — object lifecycle, job execution, validation, review, provenance,
// external sync, the upload pipeline (transfer · seal · preparation), draft, plan, publication and attempt,
// deletion, cache — so no two dimensions share one colour map and none is merged into another's badge.
//
// Five treatments, with the meaning always carried by text and a dimension-owned symbol as well as colour:
//   muted  (neutral) unavailable, unknown, not run, cancelled, no-op, superseded;
//   accent (blue)    active processing (queued, running, transferring, staging…) or an open, in-progress item;
//   warn   (amber)   a warning or a human/review gate (pending review, stale, grace period, reconciling);
//   bad    (red)     blocked, failed, rejected, inconsistent;
//   ok     (green)   only a positively verified or completed outcome (passed, accepted, published).
// A state still moving carries `moving`, and its symbol pulses (reduced motion stops the pulse).
import type { ReactNode } from "react";
import { StatusSymbol } from "./status-symbols";
import { systemLabel } from "@/lib/system-labels";
import type { components } from "@/api/client";

export type Tone = "ok" | "warn" | "bad" | "muted" | "accent";
/** One state of a dimension: its tone, the hovered sentence and, where the page shows words, its label. */
export type Term = { tone: Tone; why: string; label?: string; moving?: boolean };

/** The shared status shape: a compact semantic symbol plus readable text. Not an action and not a metadata chip. */
export function StatusLabel({ tone = "muted", title, moving, dimension, state, testid, appearance = "auto", knownState = true, children }: {
  tone?: Tone; title?: string; moving?: boolean; dimension?: string; state?: string; testid?: string; children: ReactNode;
  appearance?: "auto" | "tinted" | "plain";
  /** Internal dimension guard: unknown wire values must not acquire a positive symbol. */
  knownState?: boolean;
}) {
  const plain = appearance === "plain" || (appearance === "auto" && tone !== "warn" && tone !== "bad");
  return <span className={`status-label is-${tone}${moving ? " is-moving" : ""}${plain ? " status-label--plain" : ""}`} title={title}
    data-tone={tone} data-status={dimension} data-status-value={state} data-testid={testid}>
    <StatusSymbol dimension={dimension} state={knownState ? state : undefined} /><span className="status-label-text">{children}</span>
  </span>;
}

/** A neutral metadata chip (dataset, revision, mode, source, format): a facet, never a status. */
export function MetaChip({ mono, title, testid, children }: { mono?: boolean; title?: string; testid?: string; children: ReactNode }) {
  return <span className={`meta-chip${mono ? " is-mono" : ""}`} title={title} data-testid={testid}>{children}</span>;
}

type DimProps<K extends string> = {
  value: K | null | undefined;
  appearance?: "auto" | "tinted" | "plain";
  /** Print the sentence beside the label, not only on hover. */
  explain?: boolean;
  /** Human label by default; false exposes the exact wire value in technical surfaces. */
  labelled?: boolean;
  /** Replace the shown text; tone and sentence still come from the dimension. */
  label?: ReactNode;
  testid?: string;
};

export type Dimension<K extends string> = ((props: DimProps<K>) => ReactNode) & {
  readonly terms: Readonly<Record<K, Term>>;
  term: (value: K | null | undefined) => Term | undefined;
};

function dimension<K extends string>(name: string, terms: Record<K, Term>): Dimension<K> {
  const term = (value: K | null | undefined): Term | undefined =>
    value != null && Object.prototype.hasOwnProperty.call(terms, value) ? terms[value] : undefined;
  function Dim({ value, explain, labelled = true, label, testid, appearance }: DimProps<K>) {
    if (!value) return <StatusLabel tone="muted" title="Not recorded" dimension={name} testid={testid} appearance={appearance}>—</StatusLabel>;
    const t = term(value);
    const text = label ?? (labelled ? t?.label ?? systemLabel(value) : value);
    const badge = <StatusLabel tone={t?.tone ?? "muted"} title={t?.why} moving={t?.moving} dimension={name} state={value} knownState={Boolean(t)} testid={testid} appearance={appearance}>
      {text}</StatusLabel>;
    if (!explain || !t) return badge;
    return <span className="inline-flex items-center gap-2">{badge}<span className="text-xs text-muted">{t.why}</span></span>;
  }
  return Object.assign(Dim, { terms, term });
}

// EDA readiness is advisory availability for one immutable source, never a validation/publication gate.
export const AnalysisState = dimension("analysis", {
  not_run: { tone: "muted", label: "Not run", why: "Analysis has not run for this source." },
  waiting: { tone: "accent", label: "Waiting for spare capacity", why: "Analysis is waiting for spare execution capacity.", moving: true },
  queued: { tone: "accent", label: "Queued", why: "Analysis is waiting for a worker.", moving: true },
  running: { tone: "accent", label: "Running", why: "Analysis is measuring this exact source.", moving: true },
  partial: { tone: "warn", label: "Core ready · media incomplete", why: "Core analysis is ready; media measurements are incomplete. EDA does not gate training or publication." },
  ready: { tone: "ok", label: "Analysis ready", why: "Analysis is ready for this exact source. This does not mean validation passed, review was accepted or a version was published." },
  failed: { tone: "bad", label: "Failed", why: "Analysis failed for this source; this is not a validation finding." },
  cancelled: { tone: "muted", label: "Cancelled", why: "Analysis was cancelled." },
  unsupported: { tone: "muted", label: "Unsupported", why: "Analysis is unavailable for this source type." },
} satisfies Record<components["schemas"]["AnalysisSummary"]["state"], Term>);

// ------------------------------------------------------------------ legacy and model dimensions

// Dataset/view/base versions are published|deprecated; model and bundle versions add the
// pre-publication states. `deleted` is synthetic, from `bytes_deleted`: a tombstone, not a failure.
const LIFECYCLE: Record<string, Term> = {
  inspecting: { tone: "accent", why: "The uploaded checkpoint is being inspected.", moving: true, label: "Inspecting" },
  draft: { tone: "warn", why: "Not yet reviewed or numbered; still open to change.", label: "Draft" },
  rejected: { tone: "bad", why: "Rejected in review; it can never be published.", label: "Rejected" },
  published: { tone: "ok", why: "An immutable, numbered version. Create a new candidate to change its data.", label: "Published" },
  deprecated: { tone: "muted", why: "Skipped when resolving; still readable and restorable.", label: "Deprecated" },
  closed: { tone: "muted", why: "Closed; superseded or withdrawn.", label: "Closed" },
  deleted: { tone: "muted", why: "Its bytes were removed. The record remains.", label: "Deleted" },
};

const JOB: Record<string, Term> = {
  queued: { tone: "accent", why: "Waiting for a worker whose free capacity fits its resources.", moving: true },
  running: { tone: "accent", why: "The tool is executing.", moving: true },
  done: { tone: "ok", why: "The tool finished with a readable result — which may itself refuse." },
  failed: { tone: "bad", why: "The tool crashed, exited with an error, or wrote no output." },
  cancelled: { tone: "muted", why: "Stopped on request." },
  lost: { tone: "bad", why: "No heartbeat for three minutes." },
};

// Model checkpoint validation and legacy candidate checks. v2 data validation runs use `DataValidation`,
// where `failed` means the run itself failed; a v2 value that reaches this map is still explained correctly.
const VALIDATION: Record<string, Term> = {
  unvalidated: { tone: "muted", why: "Not yet validated against the checkpoint." },
  not_run: { tone: "muted", why: "No validation result exists yet; this is not a pass." },
  running: { tone: "accent", why: "Validation is in progress.", moving: true },
  passed: { tone: "ok", why: "Every check passed." },
  matches: { tone: "ok", why: "Outputs agree with the checkpoint within tolerance." },
  warned: { tone: "warn", why: "Findings a reviewer may accept with a reason." },
  warnings: { tone: "warn", why: "Findings a reviewer may accept with a reason." },
  blocked: { tone: "bad", why: "A blocking finding must be corrected before publication." },
  failed: { tone: "bad", why: "Not a pass: a checkpoint's outputs differ beyond tolerance, or a data validation run did not complete." },
};

const REVIEW: Record<string, Term> = {
  required: { tone: "warn", why: "A decision is needed before publication." },
  accept: { tone: "ok", why: "Accepted for publication.", label: "Accepted" },
  accepted: { tone: "ok", why: "Accepted for publication." },
  reject: { tone: "bad", why: "Rejected; this draft can never be published.", label: "Rejected" },
  rejected: { tone: "bad", why: "Rejected; this draft can never be published." },
  rework: { tone: "warn", why: "Returned to the uploader for changes." },
};

// The lineage-level wording is the contract's, verbatim (ARCHITECTURE §8, guide §6).
const PROVENANCE: Record<string, Term> = {
  verified: { tone: "ok", why: "The platform observed this training run." },
  attested: { tone: "ok", why: "The receipt matches a recorded view tree. Local training was not observed." },
  attested_unsealed: { tone: "warn", why: "The receipt has no claimed view and does not yet match a recorded tree." },
  checked: { tone: "warn", why: "Inspection is consistent with the claimed view; no receipt was supplied." },
  claimed: { tone: "warn", why: "A training view was supplied; inspection has not established consistency yet." },
  unknown: { tone: "muted", why: "No usable training origin is recorded. Publication is unavailable." },
  inconsistent: { tone: "bad", why: "The evidence conflicts with the claimed origin or isolation requirement. Publication is blocked." },
};

const SYNC: Record<string, Term> = {
  pending: { tone: "accent", why: "The external mirror has not caught up yet.", moving: true },
  synchronized: { tone: "ok", why: "The mirror matches." },
  synced: { tone: "ok", why: "The mirror matches." },
  error: { tone: "bad", why: "The mirror write failed; it will retry." },
};

// The legacy upload candidate's pipeline: normalize → measure → review → publish.
const CANDIDATE: Record<string, Term> = {
  staging: { tone: "accent", why: "Receiving the uploaded files.", moving: true },
  normalizing: { tone: "accent", why: "Converting labels to the canonical document.", moving: true },
  measuring: { tone: "accent", why: "Computing statistics.", moving: true },
  measured: { tone: "accent", why: "Measured; validation and diff pending.", moving: true },
  reviewing: { tone: "accent", why: "Validation and the diff are being computed.", moving: true },
  reviewable: { tone: "warn", why: "Ready for your review." },
  reviewed: { tone: "warn", why: "Reviewed; publication pending." },
  publishing: { tone: "accent", why: "Committing the next version.", moving: true },
  forking: { tone: "accent", why: "Publishing as a new dataset.", moving: true },
  published: { tone: "ok", why: "Published as a numbered version." },
  rejected: { tone: "bad", why: "Rejected in review." },
  failed: { tone: "bad", why: "A stage failed; see the job." },
  abandoned: { tone: "muted", why: "Abandoned; no version was published." },
  noop: { tone: "muted", why: "No change from the current version." },
};

// A labelling round in Label Studio: seeded here, opened there, brought back as a candidate.
const LABEL_ROUND: Record<string, Term> = {
  seeding: { tone: "accent", why: "Creating the Label Studio project and importing its tasks.", moving: true },
  open: { tone: "accent", why: "Open for annotators. Import it when the round is finished." },
  importing: { tone: "accent", why: "Reading the annotations back.", moving: true },
  imported: { tone: "ok", why: "Staged as an upload candidate; review and publish it like any other." },
  failed: { tone: "bad", why: "Label Studio could not be reached or refused the project." },
};

// Deletion `approved` stays amber through its grace period; an executed deletion is a tombstone, never green.
const DELETION: Record<string, Term> = {
  requested: { tone: "warn", why: "Awaiting a platform admin's approval." },
  approved: { tone: "warn", why: "Approved; deletes after the grace period." },
  executed: { tone: "muted", why: "The object's bytes were deleted." },
  cancelled: { tone: "muted", why: "The request was cancelled." },
  failed: { tone: "bad", why: "Deletion failed; it can be retried." },
};

const CACHE: Record<string, Term> = {
  present: { tone: "ok", why: "The materialized files are cached." },
  evicted: { tone: "muted", why: "Evicted from the cache; the next pull re-materializes it." },
};

export function provenanceTone(value: string | null | undefined): Tone {
  return PROVENANCE[value ?? ""]?.tone ?? "muted";
}

export const Lifecycle = dimension("lifecycle", LIFECYCLE);
export const JobState = dimension("job", JOB);
export const Validation = dimension("validation", VALIDATION);
export const ReviewState = dimension("review", REVIEW);
export const Provenance = dimension("provenance", PROVENANCE);
export const SyncState = dimension("sync", SYNC);
export const CandidateState = dimension("candidate", CANDIDATE);
export const LabelRoundState = dimension("label_round", LABEL_ROUND);
export const DeletionState = dimension("deletion", DELETION);
export const CacheState = dimension("cache", CACHE);

// ------------------------------------------------------------------ v2 upload session: transfer · seal · preparation

type Schemas = components["schemas"];
type Session = Schemas["UploadSessionDetail"];

const TRANSFER: Record<Session["state"], Term> = {
  created: { tone: "muted", why: "Admitted; no file manifest has been frozen yet." },
  transferring: { tone: "accent", why: "Receiving files; each counts only after the server verifies its bytes.", moving: true },
  paused: { tone: "warn", why: "Paused; no new bytes are accepted. Verified files are kept until the upload resumes, is cancelled or expires." },
  verifying: { tone: "accent", why: "All bytes received; the completion handoff is being verified.", moving: true },
  completed: { tone: "ok", why: "Every expected file and byte was verified by the server." },
  cancelled: { tone: "muted", why: "Cancelled; no input was sealed from this session." },
  expired: { tone: "muted", why: "Expired before completion; no input was sealed." },
  failed: { tone: "bad", why: "The transfer failed; see the session for recovery." },
};

const SEAL: Record<Session["seal_state"], Term> = {
  pending: { tone: "muted", why: "Not sealed; bytes can still be added to the frozen manifest." },
  sealing: { tone: "accent", why: "Committing the verified bytes as an immutable input.", moving: true },
  sealed: { tone: "ok", why: "Sealed: the exact input bytes are pinned by an immutable commit." },
  failed: { tone: "bad", why: "Sealing failed; verified bytes are retained for recovery." },
  reconciling: { tone: "warn", why: "Sealing is retrying from durable evidence.", moving: true },
};

const PREPARATION: Record<NonNullable<Session["preparation"]>["state"] | "none", Term> = {
  none: { tone: "muted", why: "Preparation starts after the input is sealed.", label: "not started" },
  queued: { tone: "accent", why: "Waiting for a worker.", moving: true },
  running: { tone: "accent", why: "Converting the sealed input and computing its evidence.", moving: true },
  succeeded: { tone: "ok", why: "This attempt finished. Publication is still a separate decision." },
  failed: { tone: "bad", why: "This attempt failed; the sealed input remains for a new attempt." },
  cancelled: { tone: "muted", why: "This attempt was cancelled or superseded." },
};

export const TransferState = dimension("transfer", TRANSFER);
export const SealState = dimension("seal", SEAL);
export const PreparationState = dimension("preparation", PREPARATION);

// ------------------------------------------------------------------ v2 validation, review, publication, attempt

type DataValidationValue = Session["validation"] | Schemas["DraftOut"]["validation"];

/** A validation run of an exact draft revision (or an upload's preparation findings). `failed` means the run failed. */
const DATA_VALIDATION: Record<DataValidationValue, Term> = {
  not_run: { tone: "muted", why: "No validation run exists for this exact revision yet; this is not a pass.", label: "Not run" },
  running: { tone: "accent", why: "Validation is running on this exact revision.", moving: true, label: "Running" },
  passed: { tone: "ok", why: "The validation run reported no blocking or warning rule.", label: "Passed" },
  warnings: { tone: "warn", why: "Warning rules a reviewer accepts by publishing; the review records each one.", label: "Warnings" },
  blocked: { tone: "bad", why: "A blocking rule (or a failed preparation) prevents publication; it cannot be acknowledged. Correct the content in a new revision.", label: "Blocked" },
  failed: { tone: "bad", why: "The validation run did not complete; this is not a pass. Run it again.", label: "Run failed" },
};

const REVISION_REVIEW: Record<Schemas["DraftOut"]["review"] | Session["review"], Term> = {
  not_requested: { tone: "muted", why: "No human review has been requested: checks have not produced a passing or warning result yet.", label: "Not requested" },
  pending: { tone: "warn", why: "Awaiting a reviewer's decision on this exact revision.", label: "Review pending" },
  accepted: { tone: "ok", why: "Accepted for publication; publication is still a separate step.", label: "Accepted" },
  rejected: { tone: "bad", why: "Rejected by a reviewer; this revision cannot be published.", label: "Rejected" },
  stale: { tone: "warn", why: "A newer revision, a moved head or changed checks invalidated the decision; review again.", label: "Stale" },
};

const PUBLICATION: Record<Schemas["DraftOut"]["publication"] | Session["publication"], Term> = {
  unpublished: { tone: "muted", why: "No publication attempt has been recorded; no version was published.", label: "Unpublished" },
  attempting: { tone: "accent", why: "A publication attempt is staging or finalizing.", moving: true, label: "Attempting" },
  reconciling: { tone: "warn", why: "The attempt's outcome is being reconciled; the runner retries it. Do not publish again.", moving: true, label: "Reconciling" },
  published: { tone: "ok", why: "Published as a new numbered version.", label: "Published" },
  noop: { tone: "muted", why: "Identical to the published head; the receipt points at that version and no number was allocated.", label: "No-op (no new version)" },
  failed: { tone: "bad", why: "The attempt failed; no version number was consumed.", label: "Failed" },
};

const ATTEMPT: Record<Schemas["PublicationReceipt"]["state"], Term> = {
  pending: { tone: "accent", why: "Recorded; waiting for the staging handler.", moving: true },
  staging: { tone: "accent", why: "Copying the reviewed bytes onto a staging branch.", moving: true },
  finalizing: { tone: "accent", why: "Rechecking head and review, then allocating the number under the dataset fence.", moving: true },
  reconciling: { tone: "warn", why: "The outcome is being reconciled by the sync runner; retrying it never allocates a second number.", moving: true },
  published: { tone: "ok", why: "A new version was published by this receipt." },
  noop: { tone: "muted", why: "Content equals the head; the receipt points at the existing version." },
  failed: { tone: "bad", why: "Refused or failed before the merge; no version number was consumed." },
};

export const DataValidation = dimension("data_validation", DATA_VALIDATION);
export const RevisionReviewState = dimension("revision_review", REVISION_REVIEW);
export const PublicationState = dimension("publication", PUBLICATION);
export const AttemptState = dimension("attempt", ATTEMPT);
export const attemptWhy = (state: Schemas["PublicationReceipt"]["state"]): string => ATTEMPT[state].why;

// ------------------------------------------------------------------ v2 draft and change plan

const DRAFT: Record<Schemas["DraftOut"]["state"], Term> = {
  open: { tone: "accent", why: "Open: new revisions can be bound. Nothing is published until an accepted review and a publication attempt.", label: "Open" },
  published: { tone: "ok", why: "Published as a numbered version; the draft is closed.", label: "Published" },
  abandoned: { tone: "muted", why: "Abandoned with a reason; its revisions stay readable as evidence.", label: "Abandoned" },
};

const PLAN: Record<Schemas["ChangePlanOut"]["state"], Term> = {
  computing: { tone: "accent", why: "The plan revision is being evaluated against the pinned target head.", moving: true, label: "Computing" },
  conflicts: { tone: "bad", why: "Unresolved conflicts block the candidate; resolve each one with a reason.", label: "Conflicts" },
  bindings: { tone: "warn", why: "A class or attribute binding used by a contributing sample waits on a reviewed decision; no candidate draft exists yet.", label: "Bindings to decide" },
  // A ready candidate still waits on its own validation, review and publication: a pending human gate, never green
  // (docs/architecture/UI_PHILOSOPHY.md#pending-human-gates-are-never-green).
  ready: { tone: "warn", why: "No conflict is unresolved; the candidate draft revision still goes through validation, review and publication.", label: "Candidate ready" },
  failed: { tone: "bad", why: "The plan revision could not be computed.", label: "Failed" },
  abandoned: { tone: "muted", why: "Abandoned with a reason; its revisions remain as evidence.", label: "Abandoned" },
  published: { tone: "ok", why: "The candidate was published through its own review and receipt.", label: "Published" },
};

const PLAN_REVISION: Record<Schemas["PlanRevisionOut"]["state"], Term> = {
  computing: { tone: "accent", why: "This revision is being evaluated against the pinned target head.", moving: true, label: "Computing" },
  conflicts: { tone: "bad", why: "Unresolved conflicts block this revision's candidate.", label: "Blocked by conflicts" },
  bindings: { tone: "warn", why: "A used class or attribute binding waits on a reviewed decision; this revision has no candidate yet.", label: "Waiting on bindings" },
  ready: { tone: "warn", why: "No unresolved conflict; this revision emitted its candidate draft revision, which still needs validation, review and publication.", label: "Candidate ready" },
  failed: { tone: "bad", why: "This revision could not be computed.", label: "Failed" },
  superseded: { tone: "muted", why: "A later revision of the plan replaced this one; it stays readable.", label: "Superseded" },
};

/** Currency of a draft's pinned head, separate from its lifecycle or review decision. */
export const DraftCurrency = dimension("draft_currency", {
  stale: { tone: "warn", label: "Stale draft", why: "The dataset head moved after this draft revision pinned its expected head." },
});
export const DraftState = dimension("draft", DRAFT);
export const PlanState = dimension("plan", PLAN);
export const PlanRevisionState = dimension("plan_revision", PLAN_REVISION);

// ------------------------------------------------------------------ findings

const SEVERITY: Record<Schemas["ValidationFindingV2"]["severity"], Term> = {
  block: { tone: "bad", why: "Blocking: publication is refused until the content changes; it cannot be acknowledged.", label: "Blocking" },
  warn: { tone: "warn", why: "Warning: a reviewer acknowledges this code with a reason before publication.", label: "Warning" },
  info: { tone: "muted", why: "Information only; no action is required.", label: "Information" },
};

/** A finding's severity (validation, preparation or plan finding). */
export const FindingSeverity = dimension("severity", SEVERITY);

/**
 * The upload flow's one colour per finding: red when it left samples out (the rest continues) or blocks, yellow for a
 * warning, grey for information. Reads an assessment finding (`critical | warning | info`) or a preparation or draft
 * finding (`block | warn | info` with `excluded_samples`).
 */
export type FindingColour = "blocking" | "excluded" | "warning" | "info";
export function findingColour(finding: { severity: string; excluded_samples?: number | null }): FindingColour {
  if (finding.severity === "block") return "blocking";
  if (finding.severity === "critical" || (finding.excluded_samples ?? 0) > 0) return "excluded";
  return finding.severity === "warn" || finding.severity === "warning" ? "warning" : "info";
}

const FINDING_COLOUR: Record<FindingColour, Term> = {
  blocking: SEVERITY.block,
  excluded: { tone: "bad", why: "These samples are left out; the rest continues. Nothing to acknowledge.", label: "Excluded" },
  warning: { tone: "warn", why: "Worth knowing; no sample is left out.", label: "Warning" },
  info: SEVERITY.info,
};

/** A finding as the upload flow colours it; a red one that counts samples reads "N excluded". */
export const FindingColourState = dimension("finding_colour", FINDING_COLOUR);
export function FindingColourLabel({ finding }: { finding: { severity: string; excluded_samples?: number | null } }) {
  const colour = findingColour(finding);
  const excluded = colour === "excluded" && finding.excluded_samples ? `${finding.excluded_samples.toLocaleString()} excluded` : undefined;
  return <FindingColourState value={colour} labelled label={excluded} />;
}

// ------------------------------------------------------------------ dataset diff

// One kind of change between two dataset snapshots. The colours follow the CLI's + / - / ~ marks; absence is a
// fact about a snapshot, never a delete request, so `removed` reads as a change, not a failure.
const DIFF_KIND: Record<string, Term> = {
  added: { tone: "ok", why: "Present only in the result.", label: "Added" },
  removed: { tone: "bad", why: "Absent from the result. Absence never deletes anything.", label: "Removed" },
  modified: { tone: "warn", why: "Present on both sides with a different path, box or attribute.", label: "Modified" },
  class_changed: { tone: "warn", why: "The same object carries another class; also counted as modified.", label: "Class changed" },
  unchanged: { tone: "muted", why: "Identical on both sides.", label: "Unchanged" },
};

/** The kind of one difference in a dataset comparison. */
export const DiffKind = dimension("diff_kind", DIFF_KIND);

// ------------------------------------------------------------------ model submission

const SUBMISSION: Record<Schemas["SubmissionOut"]["state"], Term> = {
  awaiting_inputs: { tone: "warn", why: "Waiting for the checkpoint and its training data.", label: "Awaiting inputs" },
  inspecting: { tone: "accent", why: "The server is inspecting the checkpoint.", label: "Inspecting", moving: true },
  awaiting_data: { tone: "warn", why: "Waiting for the training data to be resolved or registered.", label: "Awaiting data" },
  awaiting_review: { tone: "warn", why: "Ready for a person to review the data and continue.", label: "Awaiting review" },
  publishing: { tone: "accent", why: "Publishing the result.", label: "Publishing", moving: true },
  blocked: { tone: "bad", why: "A finding blocks this submission; see the findings.", label: "Blocked" },
  completed: { tone: "ok", why: "The submission finished and its model version exists.", label: "Completed" },
  cancelled: { tone: "muted", why: "Cancelled; nothing was published.", label: "Cancelled" },
};

/** The server-side state of one model submission. */
export const SubmissionState = dimension("submission", SUBMISSION);

// What the server decided to do with the data a model was trained on. Reuse changes nothing; blocked stops.
const SUBMISSION_DECISION: Record<Schemas["ExternalDataDecision"]["action"], Term> = {
  reuse: { tone: "muted", why: "The returned data matches an existing version; it is reused, nothing new is published.", label: "Reuse" },
  publish: { tone: "accent", why: "The returned data becomes the next version of its dataset.", label: "Publish" },
  fork: { tone: "accent", why: "The returned data becomes a new dataset that remembers its origin.", label: "Fork" },
  register: { tone: "accent", why: "The returned data is registered as evidence for the model without publishing it.", label: "Register" },
  blocked: { tone: "bad", why: "The returned data cannot be admitted.", label: "Blocked" },
};

/** The server's decision for a submission's data. */
export const DataDecision = dimension("data_decision", SUBMISSION_DECISION);

const ISSUE_SEVERITY: Record<Schemas["Issue-Output"]["severity"], Term> = {
  blocking: { tone: "bad", why: "Blocks the submission.", label: "Blocking" },
  warning: { tone: "warn", why: "Needs attention; does not block.", label: "Warning" },
  info: { tone: "muted", why: "Information only.", label: "Information" },
};

/** The severity of a common finding (`Issue`): blocking, warning or info. */
export const IssueSeverity = dimension("issue_severity", ISSUE_SEVERITY);

// How a locally trained model was tied to its data folder (C12). Neither level shows that the trainer read these
// bytes; that only platform training can show.
const DATA_EVIDENCE: Record<"checked" | "receipt", Term> = {
  checked: { tone: "ok", why: "The folder's bytes equal the registered version at the time of the push.", label: "Checked" },
  receipt: { tone: "warn", why: "The folder changed after its receipt; the version the receipt records was chosen and the current bytes were not checked.", label: "Receipt" },
};

/** The evidence level that ties a locally trained model to its data version. */
export const DataEvidence = dimension("data_evidence", DATA_EVIDENCE);

// Operational observation is separate from job, review and sync lifecycle dimensions.
export const WorkObservationState = dimension("work_observation", {
  queued: { tone: "accent", label: "Queued", why: "Recorded work waiting to execute.", moving: true },
  running: { tone: "accent", label: "Running", why: "The owner reports active execution.", moving: true },
  retry_wait: { tone: "warn", label: "Retry wait", why: "The external effect is scheduled for another attempt." },
  blocked: { tone: "bad", label: "Blocked", why: "The owner reports a blocker." },
  succeeded: { tone: "ok", label: "Succeeded", why: "Execution settled successfully; publication and review remain separate." },
  failed: { tone: "bad", label: "Failed", why: "The owner reports a terminal execution failure." },
  cancelled: { tone: "muted", label: "Cancelled", why: "Execution was cancelled." },
  unknown: { tone: "muted", label: "Unknown", why: "The owner cannot establish the execution outcome." },
});
export const RunnerPresence = dimension("runner_presence", {
  recent: { tone: "accent", label: "Seen recently", why: "The controller loop recently reported a snapshot; this is not application progress." },
  stale: { tone: "warn", label: "Stale signal", why: "No controller snapshot for at least three heartbeat periods." },
  no_recent_signal: { tone: "warn", label: "No recent signal", why: "No snapshot for at least six heartbeat periods; work state is unchanged." },
  stopped: { tone: "muted", label: "Stopped", why: "The controller reported an orderly stop." },
  unknown: { tone: "muted", label: "Unknown", why: "Presence could not be established from the timestamp." },
});
